/**
 * HTTP client for object repository file APIs.
 */
export class RepoApiClient {
  constructor({ workspaceId, objectType, objectId, taskId = null, fetchFn, apiBase = '' }) {
    this.workspaceId = workspaceId;
    this.objectType = objectType;
    this.objectId = objectId;
    this.taskId = taskId;
    this.fetchFn = fetchFn || fetch.bind(window);
    this.apiBase = apiBase.replace(/\/$/, '');
  }

  _basePath() {
    return `${this.apiBase}/workspaces/${this.workspaceId}/repos/${this.objectType}/${this.objectId}`;
  }

  _query(extra = {}) {
    const params = new URLSearchParams();
    if (this.taskId) params.set('task_id', this.taskId);
    Object.entries(extra).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value);
      }
    });
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }

  async metadata() {
    const res = await this.fetchFn(`${this._basePath()}${this._query()}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async init(defaultBranch = 'main') {
    const res = await this.fetchFn(
      `${this._basePath()}/init${this._query({ default_branch: defaultBranch })}`,
      { method: 'POST' },
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async listTree(path = '') {
    const res = await this.fetchFn(`${this._basePath()}/tree${this._query({ path })}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async readFile(filePath) {
    const res = await this.fetchFn(`${this._basePath()}/files/${filePath}${this._query()}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async writeFile(filePath, content, { encoding = 'utf-8', message } = {}) {
    const res = await this.fetchFn(`${this._basePath()}/files/${filePath}${this._query()}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, encoding, message }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async mkdir(path, message = 'Create directory via repo editor') {
    const res = await this.fetchFn(`${this._basePath()}/mkdir${this._query({ path })}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async deletePath(filePath) {
    const res = await this.fetchFn(`${this._basePath()}/files/${filePath}${this._query()}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async diff(baseSha) {
    const res = await this.fetchFn(`${this._basePath()}/diff${this._query({ base_sha: baseSha })}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }
}
